import { CLIENT_ID, CLIENT_ID_HEADER } from "./clientId";
import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";
import { SearchUnavailableError } from "./search";

// Mirrors the host's note DTOs (Nihongonator.Web/Dtos.cs), serialized camelCase.

/** A media field: the raw stored value ([sound:…] / <img>) plus the parsed filename. */
export interface MediaRef {
  raw: string;
  filename: string | null;
}

/** Full single-note detail for the editor (GET /api/notes/{id}). */
export interface NoteField {
  /** The Anki field name — `Meaning`, `Part of Speech`. English, as Anki names it. */
  name: string;
  value: string;
}

export interface NoteDetail {
  noteId: number;
  japanese: string;
  reading: string;
  english: string;
  partOfSpeech: string;
  extra: string;
  frequency: string;
  sentence: string;
  audio: MediaRef;
  sentenceAudio: MediaRef;
  image: MediaRef;
  extraImage: MediaRef;
  tags: string[];
  /** The JMdict `ent_seq` values this card was built from, comma-separated. */
  entries: string;
  /**
   * Why this card is deliberately out of step with JMdict — prose, or nothing.
   *
   * **`null` and `""` are different, and the difference is load-bearing.** `null` means the
   * notetype has no such field; `""` means it has one and it is empty. Send the key back only when
   * you read a non-null value: the server validates against the live note's own field names and
   * rejects the *whole* save — every field, not just this one — if handed a name the notetype
   * lacks. `noteFieldsFor` and `noteUpdateBody` are what keep both panels honest about it.
   */
  dictionaryDivergence: string | null;
  /**
   * The text fields this note's editor offers, in order (日本-498).
   *
   * **Notetype-driven, and it is what both editing panels render from.** The named keys above are
   * `Genki (Apoc)`'s and stay because the whole app reads them — the browse row's gloss, the
   * dock's heading, the media slots — but a note on any other notetype has content under names
   * that have no key here, and no client could see any of it before this. `Grammar (Apoc)` was
   * the case that exposed it (its whole content was `Meaning`, `Usage`, `Formation`, `Source`,
   * `Example` and `Related`); 日本-519 removed that notetype and this inversion outlives it,
   * because a seventh notetype now costs no client change at all.
   *
   * Names are ANKI's, and stay English for the reason every field name in this app does: it is
   * what you would type into Anki's own browser.
   *
   * Empty for a notetype this app has no editor design for — which is exactly what `editable`
   * reports, so the two cannot disagree.
   */
  fields: NoteField[];
  /** The notetype's name, so a surface can say what it is holding rather than guess. */
  noteModel: string;
  /**
   * Whether the text-field editors apply. False for any notetype but the configured one.
   *
   * `by-term` searches `Japanese:<term>`, which is a FIELD search — so a note of another notetype
   * that happens to carry that field is findable, would open in a panel built for the six Genki
   * fields, and would then 400 on save. Ask before opening, not after.
   */
  editable: boolean;
}

/** PUT body — the 8 editable text fields + tags (media is edited separately). */
export interface UpdateNoteBody {
  // Every field is optional: the API applies only what is sent, which is what lets Read's
  // expand-in-place panel save the handful of fields it shows without having to round-trip
  // and resend the rest.
  japanese?: string;
  reading?: string;
  english?: string;
  partOfSpeech?: string;
  extra?: string;
  frequency?: string;
  sentence?: string;
  /** Only ever sent when the note answered non-null — see `NoteDetail.dictionaryDivergence`. */
  dictionaryDivergence?: string;
  /**
   * Fields by their ANKI names — what both editing panels send since 日本-498.
   *
   * The named keys above still work and are still what an older tab sends; this is merged over
   * them server-side. It exists because a note on a notetype other than `Genki (Apoc)` has fields
   * with no named key here, and adding six more per notetype is the hardcoding the non-goals warn
   * against.
   */
  fields?: Record<string, string>;
  tags?: string[];
}

/** One of the four media fields on the note. */
export type MediaField = "Audio" | "SentenceAudio" | "Image" | "ExtraImage";
export type MediaKind = "audio" | "image";

export interface UploadMediaBody {
  field: MediaField;
  kind: MediaKind;
  filename: string;
  /** Omit to link a file already in the media store rather than uploading one — see `linkMedia`. */
  dataB64?: string;
}

/**
 * A failed request, carrying the status so a caller can tell "no" from "broken".
 *
 * Every rejection from this module is one of these, and the message is still the server's, so
 * `error.message` reads exactly as it did before this existed.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function errorMessage(res: Response): Promise<string> {
  bounceIfUnauthorized(res);
  try {
    const body = await res.json();
    // FastAPI's HTTPExceptions arrive as {detail}; only the app-level 502/401 handlers use {error}.
    if (body && typeof body.detail === "string") return body.detail;
    if (body && typeof body.error === "string") return body.error;
  } catch {
    // non-JSON body
  }
  return `Request failed (${res.status})`;
}

async function throwIfUnavailable(res: Response): Promise<void> {
  if (res.status !== 503) return;
  let state = "loading";
  let reason = "Loading dictionary…";
  try {
    const body = await res.json();
    if (body?.state) state = body.state;
    if (body?.reason) reason = body.reason;
  } catch {
    // non-JSON body
  }
  throw new SearchUnavailableError(reason, state);
}

async function sendJson<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      // Says "this tab has already accounted for this change locally", so the collectionChanged
      // frame it provokes is ignored here and acted on everywhere else. See `clientId.ts`.
      [CLIENT_ID_HEADER]: CLIENT_ID,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
  });
  await throwIfUnavailable(res);
  if (!res.ok) throw new ApiError(await errorMessage(res), res.status);
  return res.json();
}

/**
 * Two different questions about a deck, and picking the wrong one is a lie rather than a rounding.
 *
 * `new` / `due` / `total` are the **uncapped** pile. Read's top bar shows `new`, and the design's
 * `· 23d` runway divides it by the new-per-day limit, so it has to stay uncapped.
 *
 * `dueToday` / `newToday` are **capped** by the deck's limits and its parents' — what the scheduler
 * would actually hand you. That is the Review badge, and it is not `due`.
 */
export interface DeckCounts {
  deck: string;
  new: number;
  due: number;
  total: number;
  dueToday: number;
  newToday: number;
  /**
   * Notes added to this deck today — Read's `3 added`.
   *
   * The odd one out: the others describe the pile, this describes what you did. It rides along
   * because Read already fetches this payload on focus. "Today" is Anki's day, which rolls at the
   * collection's cutoff — not at midnight, and not when the page last loaded.
   */
  addedToday: number;
}

/** Defaults to the configured vocab deck — no deck name is hardcoded on this side either. */
export function deckCounts(deck?: string): Promise<DeckCounts> {
  const query = deck ? `?deck=${encodeURIComponent(deck)}` : "";
  return sendJson<DeckCounts>("GET", `/api/decks/counts${query}`);
}

/** Read one note by its Anki note id. */
export function getNote(id: number): Promise<NoteDetail> {
  return sendJson<NoteDetail>("GET", `/api/notes/${id}`);
}

/** Read the note whose Japanese field is exactly this term (the search "open" key). */
export function getNoteByTerm(term: string): Promise<NoteDetail> {
  return sendJson<NoteDetail>("GET", `/api/notes/by-term?term=${encodeURIComponent(term)}`);
}

/**
 * `getNoteByTerm`, but "there is no note" answers `null` rather than throwing.
 *
 * Not carded is an ordinary answer, and the caller has a different thing to say about it than
 * about the hub being down. Mirrors `kanjiDetail` in `search.ts`.
 *
 * Only a 404 becomes `null`. A **409** — the term is carded, but under a notetype this API's
 * editors do not understand — keeps throwing, because its message names that notetype and is the
 * most useful thing anyone can say at that point.
 */
export async function getNoteByTermOrNull(term: string): Promise<NoteDetail | null> {
  try {
    return await getNoteByTerm(term);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** Save edited text fields + tags. Returns the updated note. */
export function updateNote(id: number, body: UpdateNoteBody): Promise<NoteDetail> {
  return sendJson<NoteDetail>("PUT", `/api/notes/${id}`, body);
}

/** Upload an audio/image file and point the given field at it. Returns the updated note. */
export function uploadMedia(id: number, body: UploadMediaBody): Promise<NoteDetail> {
  return sendJson<NoteDetail>("POST", `/api/notes/${id}/media`, body);
}

/**
 * Put a file on an existing note's media field, in one call.
 *
 * The counterpart to `storeMedia`, and the difference is whether there is a note yet. The reading
 * log has to store-then-quote because a sentence is not a note and may never become one; a card
 * already exists, so one request writes the bytes and points the field at them inside a single
 * collection write — one sync, and no window where the bytes are stored but unreferenced.
 */
export async function attachMedia(
  id: number,
  field: MediaField,
  file: File,
  filename: string,
): Promise<NoteDetail> {
  return uploadMedia(id, {
    field,
    kind: field === "Audio" || field === "SentenceAudio" ? "audio" : "image",
    filename,
    dataB64: await fileToBase64(file),
  });
}

/** What `storeMedia` gives back: the stored name, the field value, and a URL to render it. */
export interface StoredMedia {
  filename: string;
  /** e.g. `<img src="vn-1.png">` — hand straight to addCard's image / extraImage. */
  value: string;
  url: string;
}

/**
 * Store a file with no note attached yet.
 *
 * The reading log's image slots need this: screenshots are pasted onto a *sentence*, which is not a
 * note and may never become one. Cards mined from that sentence inherit the field value.
 */
export function storeMedia(
  filename: string,
  dataB64: string,
  kind: MediaKind = "image",
): Promise<StoredMedia> {
  return sendJson<StoredMedia>("POST", "/api/media", { kind, filename, dataB64 });
}

/**
 * Point a note's media field at a file that is **already** in the collection's media store.
 *
 * The bytes-free sibling of `attachMedia`, and what makes dragging an image from one slot to another
 * cheap. Re-uploading the bytes instead would write a second copy of an identical file for what is
 * purely a reordering — and 日本-301 is already open about media duplication.
 *
 * The server refuses a name it cannot find, rather than writing a field that points at nothing.
 */
export function linkMedia(id: number, field: MediaField, filename: string): Promise<NoteDetail> {
  return uploadMedia(id, {
    field,
    kind: field === "Audio" || field === "SentenceAudio" ? "audio" : "image",
    filename,
  });
}

/** Clear a media field (remove the image/audio link). Returns the updated note. */
export function clearMedia(id: number, field: MediaField): Promise<NoteDetail> {
  return sendJson<NoteDetail>("DELETE", `/api/notes/${id}/media?field=${encodeURIComponent(field)}`);
}

/** The prefix a *staged* capture wears inside a slot value: `<img src="capture:<id>">`. */
export const CAPTURE_SCHEME = "capture:";

/**
 * URL that serves a media file's bytes (for inline `<img>`/`<audio>`).
 *
 * **The single place a slot's reference becomes a URL**, which is what let capture staging happen
 * without touching every surface that renders an image. A capture screenshot is not in the
 * collection until something is mined from its sentence — until then the slot holds
 * `capture:<id>` and the bytes come from the staging store, not from `col.media`.
 *
 * Routing here rather than at each `<img>` keeps `mediaSlots.ts`'s rule intact: a slot still holds
 * the field value, and mining is still a verbatim copy of it. The server promotes on the way past.
 */
export function mediaUrl(filename: string): string {
  if (filename.startsWith(CAPTURE_SCHEME)) {
    return `${API_BASE}/api/captures/${encodeURIComponent(filename.slice(CAPTURE_SCHEME.length))}`;
  }
  return `${API_BASE}/api/media?name=${encodeURIComponent(filename)}`;
}

/** Read a File as base64 (no data: prefix) for the upload endpoint. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * Hard delete, no trash (PRD decision 3). Used by "Undo add" in Read and by the Cards dock, where
 * it is confirmation-gated. Undo-add is not gated: you added the card a second ago on purpose.
 */
export async function deleteNote(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/notes/${id}`, {
    method: "DELETE",
    headers: { [CLIENT_ID_HEADER]: CLIENT_ID },
  });
  if (!res.ok) throw new Error(await errorMessage(res));
}
